import type { MeteringVariantField } from '@nadoutong/contracts';
import {
  VIDEO_INPUT_VARIANT_VALUES,
  meteringCapabilityForProfileKind,
  resolveVariantKey,
  variantFieldsInTemplate,
  variantValuesFromKey,
} from '@nadoutong/contracts';
import { useT } from '../i18n/index.ts';
import type { I18nKey } from '../i18n/index.ts';
import { FormField, Input, Select } from '../ui/index.ts';
import type { SelectOption } from '../ui/index.ts';

/**
 * Per-field tier key entry (#1883, parent #1838).
 *
 * The owner's complaint this closes: a tier key used to be one free-text
 * input, so the operator had to know — from nowhere — that the key for a
 * video order at 480P is spelled `includesvideoinput-480p`, and a typo was
 * not an error but a tier nobody's requests resolve to, priced beside the
 * tier that has no row. The repair is that the KEY IS NEVER TYPED: the spec's
 * own `variant_key_template` decides which fields exist, each field gets its
 * own control (`has_video_input` a closed two-value select, the open-domain
 * fields a value input), and the full key is derived through the contracts'
 * OWN `resolveVariantKey` — the same function the gateway's route gate uses —
 * so the console cannot learn a second spelling of the composition rule.
 */

export type VariantFieldValues = Readonly<Partial<Record<MeteringVariantField, string>>>;

const FIELD_LABEL_KEYS: Readonly<Record<MeteringVariantField, I18nKey>> = {
  size: 'metering.variant_field.size',
  quality: 'metering.variant_field.quality',
  resolution: 'metering.variant_field.resolution',
  has_video_input: 'metering.variant_field.has_video_input',
};

/**
 * The alphabet a canonical variant VALUE may use — the same `[a-z0-9]`-only
 * rule the gateway's canonicalizers enforce, restated because a value typed
 * here must compose into a key the price write face will accept.
 */
const CANONICAL_VARIANT_VALUE = /^[a-z0-9]+$/;

export function normalizeVariantValue(raw: string): string {
  return raw.trim().toLowerCase();
}

/**
 * Compose the tier key for one row from its per-field values, through the
 * contracts' own resolution rule. `null` means the row is not yet a key:
 * a field is empty, or a value uses characters no canonical value may carry.
 */
export function composeVariantKey(
  template: string,
  values: VariantFieldValues,
): string | null {
  const fields = variantFieldsInTemplate(template);
  const entries: [MeteringVariantField, string][] = [];
  for (const field of fields) {
    const value = normalizeVariantValue(values[field] ?? '');
    if (!CANONICAL_VARIANT_VALUE.test(value)) return null;
    entries.push([field, value]);
  }
  const resolved = resolveVariantKey(template, {
    variant: Object.fromEntries(entries),
    // The console previews the key of a request the gateway will canonicalize
    // and overwrite; per-request egress refusals are the route gate's job.
    variantEgress: 'canonical_overwrite',
  });
  return resolved.ok ? resolved.key : null;
}

/**
 * Parse a stored key back into its per-field values, so a prefilled form can
 * carry existing tiers forward without asking the operator to re-derive what
 * the console itself composed. `null` when the key does not match the
 * template. This is the contracts' own inverse (`variantValuesFromKey`) —
 * the same one the gateway's task settlement recovers a frozen tier with —
 * so the console cannot learn a second spelling of the decomposition rule
 * any more than of the composition one.
 */
export function parseVariantKey(
  template: string,
  key: string,
): VariantFieldValues | null {
  return variantValuesFromKey(template, key);
}

export function allVariantValuesEmpty(
  template: string,
  values: VariantFieldValues,
): boolean {
  return variantFieldsInTemplate(template).every(
    (field) => (values[field] ?? '').trim().length === 0,
  );
}

interface VariantFieldInputsProps {
  readonly template: string;
  readonly values: VariantFieldValues;
  readonly onChange: (values: VariantFieldValues) => void;
  readonly disabled: boolean;
  readonly idPrefix: string;
}

/** One control per template field; the composed key is previewed beside. */
export function VariantFieldInputs({
  template,
  values,
  onChange,
  disabled,
  idPrefix,
}: VariantFieldInputsProps): JSX.Element {
  const t = useT();
  const fields = variantFieldsInTemplate(template);
  return (
    <>
      {fields.map((field) => {
        const fieldId = `${idPrefix}-${field}`;
        const label = t(FIELD_LABEL_KEYS[field]);
        if (field === 'has_video_input') {
          const current = values[field] ?? '';
          const canonical: readonly string[] = [
            VIDEO_INPUT_VARIANT_VALUES.absent,
            VIDEO_INPUT_VARIANT_VALUES.present,
          ];
          // A prefilled value outside the closed pair (a key written through
          // another surface) keeps a plain value input rather than a select
          // with a synthetic option: it stays visible and re-submittable, and
          // a dynamically-grown option list is exactly what makes the
          // underlying primitive reset a controlled value to ''.
          if (current.length > 0 && !canonical.includes(current)) {
            return (
              <FormField key={field} label={label} fieldId={fieldId}>
                <Input
                  id={fieldId}
                  type="text"
                  mono
                  value={current}
                  onChange={(e) => onChange({ ...values, [field]: e.currentTarget.value })}
                  data-testid={fieldId}
                  disabled={disabled}
                />
              </FormField>
            );
          }
          const options: SelectOption[] = [
            {
              value: VIDEO_INPUT_VARIANT_VALUES.absent,
              label: `${t('metering.video_input.absent')} (${VIDEO_INPUT_VARIANT_VALUES.absent})`,
            },
            {
              value: VIDEO_INPUT_VARIANT_VALUES.present,
              label: `${t('metering.video_input.present')} (${VIDEO_INPUT_VARIANT_VALUES.present})`,
            },
          ];
          return (
            <FormField key={field} label={label} fieldId={fieldId}>
              <Select
                id={fieldId}
                value={current.length === 0 ? undefined : current}
                onValueChange={(next) => onChange({ ...values, [field]: next })}
                options={options}
                placeholder={t('metering.variant_field.select_placeholder')}
                disabled={disabled}
                ariaLabel={label}
                testId={fieldId}
              />
            </FormField>
          );
        }
        return (
          <FormField key={field} label={label} fieldId={fieldId}>
            <Input
              id={fieldId}
              type="text"
              mono
              value={values[field] ?? ''}
              onChange={(e) => onChange({ ...values, [field]: e.currentTarget.value })}
              data-testid={fieldId}
              disabled={disabled}
              placeholder={t('metering.variant_field.value_placeholder')}
            />
          </FormField>
        );
      })}
    </>
  );
}

interface VariantKeyPreviewProps {
  readonly template: string;
  readonly values: VariantFieldValues;
  readonly testId: string;
  /** When true, an all-empty row previews as the default (`''`) price row. */
  readonly emptyMeansDefault?: boolean;
}

export function VariantKeyPreview({
  template,
  values,
  testId,
  emptyMeansDefault = false,
}: VariantKeyPreviewProps): JSX.Element {
  const t = useT();
  if (emptyMeansDefault && allVariantValuesEmpty(template, values)) {
    return (
      <p className="m-0 text-micro text-paper-low" data-testid={testId}>
        {t('metering.variant_key.preview_default')}
      </p>
    );
  }
  const key = composeVariantKey(template, values);
  if (key === null) {
    return (
      <p className="m-0 text-micro text-paper-low" data-testid={testId}>
        {t('metering.variant_key.preview_incomplete')}
      </p>
    );
  }
  return (
    <p className="m-0 text-micro text-paper-low" data-testid={testId}>
      {t('metering.variant_key.preview')}
      <span className="ml-1 font-mono text-paper-mid">{key}</span>
    </p>
  );
}

interface MeteringShapeSummaryProps {
  /** Tolerant of a stale stored string, like the contracts lookup itself. */
  readonly profile: string;
  /** The governing template, when one is stored — overrides the fixed one. */
  readonly template?: string | undefined;
  readonly testId?: string;
}

/**
 * The billing form each fixed-template profile sells, in the owner's own
 * words — the profile decides the shape, so the line is WRITTEN per profile
 * rather than composed from a field list an operator might mistake for a
 * choice.
 */
const FIXED_SHAPE_LABEL_KEYS: Readonly<Partial<Record<string, I18nKey>>> = {
  ark_video_token_v1: 'metering.shape.fixed.ark_video_token_v1',
  ark_video_token_tiered_v1: 'metering.shape.fixed.ark_video_token_tiered_v1',
  duration_seconds_v1: 'metering.shape.fixed.duration_seconds_v1',
  ark_video_seconds_v1: 'metering.shape.fixed.ark_video_seconds_v1',
  openai_images_unit_v1: 'metering.shape.fixed.openai_images_unit_v1',
  ark_video_seconds_ref_v1: 'metering.shape.fixed.ark_video_seconds_ref_v1',
  grok_video_seconds_v1: 'metering.shape.fixed.grok_video_seconds_v1',
  grok_image_unit_v1: 'metering.shape.fixed.grok_image_unit_v1',
};

/**
 * The read-only 计费形态 line, derived from `PROFILE_METERING_CAPABILITY`.
 *
 * A profile with a FIXED tier template shows its own fixed wording (the sales
 * form's billing shape is the profile's, not a per-registration choice); the
 * generic lane + fields composition remains for everything else, and for the
 * legacy case where a stored template diverges from the fixed one — there the
 * stored document is what the gateway actually resolves tiers by, so the line
 * must not claim the fixed shape.
 */
export function MeteringShapeSummary({
  profile,
  template,
  testId,
}: MeteringShapeSummaryProps): JSX.Element {
  const t = useT();
  const capability = meteringCapabilityForProfileKind(profile);
  const fixedKey = FIXED_SHAPE_LABEL_KEYS[profile];
  const shownTemplate = template ?? capability.fixedVariantKeyTemplate ?? undefined;
  if (
    fixedKey !== undefined &&
    (template === undefined || template === capability.fixedVariantKeyTemplate)
  ) {
    return (
      <p className="m-0 text-micro text-paper-low" data-testid={testId}>
        {t(fixedKey)}
        {shownTemplate !== undefined ? (
          <span className="ml-1 font-mono text-paper-mid">{shownTemplate}</span>
        ) : null}
      </p>
    );
  }
  const fields = variantFieldsInTemplate(shownTemplate);
  const lane =
    capability.lane === 'unit'
      ? t('metering.shape.lane_unit')
      : t('metering.shape.lane_token');
  const tiers =
    fields.length === 0
      ? t('metering.shape.no_tiers')
      : t('metering.shape.tiered_by', {
          fields: fields.map((field) => t(FIELD_LABEL_KEYS[field])).join(' + '),
        });
  return (
    <p className="m-0 text-micro text-paper-low" data-testid={testId}>
      {t('metering.shape.summary', { lane, tiers })}
      {template !== undefined ? (
        <span className="ml-1 font-mono text-paper-mid">{template}</span>
      ) : null}
    </p>
  );
}
